import { useMemo, useRef, useState } from 'react';
import type { InputProps } from 'antd';

type CountConfig = NonNullable<InputProps['count']>;

/** 截断到 max 个字符（按 UTF-16 计，与后端 String.length 一致），不把 emoji 等代理对切成半个 */
export const truncateText = (value: string, max: number): string => {
    if (value.length <= max) {
        return value;
    }
    const cut = value.slice(0, max);
    return /[\uD800-\uDBFF]$/.test(cut) ? cut.slice(0, -1) : cut;
};

/**
 * 带字数上限的输入框在中文输入法下的处理：
 * - 选字期间（拼音还没上屏）计数封顶到上限，不显示 23/20、不标红；
 * - 选字结束时按真实长度截断到上限（部分浏览器选字上屏不受原生 maxLength 约束）；
 * - composingRef 供表单校验在选字期间跳过，onCompositionDone 在选字结束后回调（如补一次校验）。
 *
 * 用法：`<Input {...ime.inputProps} showCount />`，不要再传 maxLength（由 count.max 统一控制）。
 */
export const useImeSafeCount = (max: number, onCompositionDone?: () => void) => {
    const composingRef = useRef(false);
    // 只为选字开始 / 结束时触发重渲染刷新计数，取值一律读 ref
    const [, setComposing] = useState(false);

    const count = useMemo<CountConfig>(() => ({
        max,
        strategy: (value: string) => (composingRef.current ? Math.min(value.length, max) : value.length),
        exceedFormatter: (value: string, config: { max: number }) => truncateText(value, config.max),
    }), [max]);

    const inputProps = {
        count,
        onCompositionStart: () => {
            composingRef.current = true;
            setComposing(true);
        },
        // 捕获阶段先于 rc-input 自己的 compositionEnd 处理执行：它随后按真实长度判断超限并截断
        onCompositionEndCapture: () => {
            composingRef.current = false;
            setComposing(false);
        },
        onCompositionEnd: () => {
            onCompositionDone?.();
        },
    };

    return { composingRef, inputProps };
};
